import React, { createContext, useContext } from "react";
import {
  Drawer as MantineDrawer,
  DrawerProps as MantineDrawerProps,
  Box,
  BoxProps,
  Button,
} from "@mantine/core";
import cx from "clsx";
import styles from "@scss/custom-components/drawer.module.scss";

/* ---------- Contexts ---------- */

type DrawerVariant = "default" | "ai";

const DrawerVariantContext = createContext<DrawerVariant>("default");
const FooterVisibleContext = createContext<boolean>(true);
const DrawerCloseContext = createContext<(() => void) | undefined>(undefined);

/* ---------- Main component ---------- */

export interface CwiggoDrawerProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof MantineDrawer>,
    "classNames"
  > {
  /** Hide overlay when false (also passed to Mantine's withOverlay) */
  overlay?: boolean;
  /** Visual variant for Body/Footer styling switches */
  variant?: DrawerVariant;
  /** Show/hide footer area */
  footer?: boolean;
  className?: string;
  /** Forward slot overrides to Mantine Drawer (v8 slots: overlay, content, body) */
  classNames?: MantineDrawerProps["classNames"];
  larger?: boolean;
}

type DrawerComponent = React.FC<CwiggoDrawerProps> & {
  Header: React.FC<CwiggoDrawerHeaderProps>;
  Body: React.FC<CwiggoDrawerBodyProps>;
  Footer: React.FC<CwiggoDrawerFooterProps>;
};

export const CustomDrawer: DrawerComponent = ({
  overlay = true,
  variant = "default",
  footer = true,
  position = "right",
  larger = false,
  className,
  classNames: classNamesProp = {},
  children,
  onClose,
  ...props
}) => {
  const classNames = {
    root: styles.drawerRoot,
    overlay: cx(styles.overlay, overlay === false && styles.hide),
    content: cx(
      styles.drawerContent,
      larger && styles["drawerContent--larger"],
    ),
    body: styles.drawerBody,
    ...classNamesProp,
  };

  return (
    <DrawerCloseContext.Provider value={onClose}>
      <DrawerVariantContext.Provider value={variant}>
        <FooterVisibleContext.Provider value={footer}>
          <MantineDrawer
            withOverlay={overlay}
            withCloseButton={false}
            position={position}
            onClose={onClose}
            className={className}
            classNames={classNames}
            {...props}
          >
            {/* Prevent auto-focus stealing */}
            <div
              data-autofocus
              style={{
                position: "fixed",
                width: 0,
                height: 0,
                overflow: "hidden",
              }}
            />
            {children}
          </MantineDrawer>
        </FooterVisibleContext.Provider>
      </DrawerVariantContext.Provider>
    </DrawerCloseContext.Provider>
  );
};

/* ---------- Sub-components ---------- */

export interface CwiggoDrawerHeaderProps extends BoxProps {
  topAction?: "close" | "back";
  /** Optional explicit onClose; defaults to the drawer's onClose via context */
  onClose?: () => void;
  backUrl?: string;
  className?: string;
  children?: React.ReactNode;
}

const Header: React.FC<CwiggoDrawerHeaderProps> = ({
  topAction = "close",
  onClose,
  backUrl,
  className,
  children,
  ...props
}) => {
  const ctxOnClose = useContext(DrawerCloseContext);
  const close = onClose ?? ctxOnClose;

  const handleBack = () => {
    if (close) return close();
    if (backUrl) {
      window.location.href = backUrl;
      return;
    }
    window.history.back();
  };

  return (
    <Box component="header" className={cx(styles.header, className)} {...props}>
      {topAction === "close" && close && (
        <Button
          variant="ghost"
          size="sm"
          className={styles.topButton}
          rightSection={<></>}
          onClick={close}
        >
          CLOSE
        </Button>
      )}
      {topAction === "back" && (
        <Button
          variant="ghost"
          size="sm"
          className={styles.topButton}
          leftSection={<></>}
          onClick={handleBack}
        >
          BACK
        </Button>
      )}
      {children}
    </Box>
  );
};
Header.displayName = "CustomDrawer.Header";

export interface CwiggoDrawerBodyProps extends BoxProps {
  className?: string;
  children?: React.ReactNode;
}

const Body: React.FC<CwiggoDrawerBodyProps> = ({
  className,
  children,
  ...props
}) => {
  const variant = useContext(DrawerVariantContext);

  const bodyClass = cx(
    styles.body,
    variant === "ai" && styles["body--ai"],
    className,
  );

  return (
    <Box component="section" className={bodyClass} {...props}>
      {children}
    </Box>
  );
};
Body.displayName = "CustomDrawer.Body";

export interface CwiggoDrawerFooterProps extends BoxProps {
  className?: string;
  children?: React.ReactNode;
}

const Footer: React.FC<CwiggoDrawerFooterProps> = ({
  className,
  children,
  ...props
}) => {
  const variant = useContext(DrawerVariantContext);
  const visible = useContext(FooterVisibleContext);

  const footerClass = cx(
    styles.footer,
    variant === "ai" && styles["footer--ai"],
    visible === false && styles.hide,
    className,
  );

  return (
    <Box component="footer" className={footerClass} {...props}>
      {children}
    </Box>
  );
};
Footer.displayName = "CustomDrawer.Footer";

/* ---------- Attach statics ---------- */

CustomDrawer.Header = Header;
CustomDrawer.Body = Body;
CustomDrawer.Footer = Footer;
